import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { Link, router } from '@inertiajs/react'
import { ChevronDown, Plus, Search, CornerDownLeft, Check, Crown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buildSwitcherRows } from '@/lib/groups'
import type { AccountPropertyGroup, CurrentAccount, PropertyGroupLens } from '@/types/shared'

// `portfolio` — root briefing header: a "Jump to property" search trigger, no current property.
// `property`  — per-property header: the trigger shows the CURRENT property name and the list
//               highlights it. Both modes share the same ⌘K handler, search field, keyboard
//               nav, and a flat list SCOPED to the current business (cross-business movement
//               is handled by AccountSwitcher / "Switch business", never listed here).
type JumpToPropertyMode = 'portfolio' | 'property'

interface JumpToPropertyProps {
  // Account-grouped shape (tenancy-14). Defaulted to [] so a briefly-absent prop never crashes.
  properties?: AccountPropertyGroup[]
  // The business currently in context. The jump is scoped to THIS business only — each
  // account is a separate portfolio and properties are never listed across businesses.
  currentAccount?: CurrentAccount | null
  mode?: JumpToPropertyMode
  // property mode only: drives the trigger label and the highlighted row.
  currentProperty?: { id: number; name: string } | null
}

interface FlatProperty {
  id: number
  name: string
  /** Backend-served canonical path (`PropertyOption.basePath`) — every row links straight to it. */
  basePath: string
  currency?: string
  // The PropertyGroup this hotel belongs to, or null when it is ungrouped (which is every hotel in a
  // business that does not use groups — the shape this list had before groups existed).
  group?: PropertyGroupLens | null
}

// Shared property jump for BOTH headers, mirroring AccountSwitcher's `mode` pattern.
// Scoped to the current business, filter-as-you-type, ⌘K to open. Selecting a property
// navigates (SPA <Link>) into it. Because the list is a single business's properties, it is
// a flat list with no account-group headers. In `property` mode the trigger shows the current
// property name and its row is highlighted; in `portfolio` mode there is no current property,
// so the trigger is a "Jump to property" search affordance and no row is pre-highlighted.
export default function JumpToProperty({ properties, currentAccount, mode = 'portfolio', currentProperty }: JumpToPropertyProps) {
  const groups = properties ?? []
  const isProperty = mode === 'property'
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  // -1 = no active row (no selection until the user navigates with arrows or hovers).
  const [activeIdx, setActiveIdx] = useState(-1)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ top: 0, right: 0 })

  // Scope to the current business only. Fall back to the first group during the brief
  // window before the concurrent backend props land, so the jump is never empty when the
  // user actually has properties.
  const scopedGroup = useMemo(() => {
    if (groups.length === 0) return null
    return groups.find((g) => g.accountId === currentAccount?.id) ?? groups[0]
  }, [groups, currentAccount?.id])

  // Flat, searchable index for this business — used for filtering + keyboard navigation.
  const flat: FlatProperty[] = useMemo(
    () =>
      (scopedGroup?.properties ?? []).map((p) => ({
        id: p.id,
        name: p.name,
        basePath: p.basePath,
        currency: p.currency,
        group: p.group ?? null,
      })),
    [scopedGroup],
  )

  // Group structure: a lead with its members nested underneath, so a unit is visible as a unit. While
  // a search is active the list goes flat (indenting a member under a lead the filter removed would
  // indent it under nothing) and each grouped row names its group instead. See buildSwitcherRows.
  const rows = useMemo(() => buildSwitcherRows(flat, query), [flat, query])
  const matches = useMemo(() => rows.map((row) => row.property), [rows])

  const updatePos = useCallback(() => {
    if (!buttonRef.current) return
    const rect = buttonRef.current.getBoundingClientRect()
    setPos({ top: rect.bottom + 6, right: window.innerWidth - rect.right })
  }, [])

  // ⌘K / Ctrl+K toggles the menu globally.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen((prev) => !prev)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    if (!open) {
      setQuery('')
      setActiveIdx(-1)
      return
    }
    updatePos()
    // Focus the search field on open.
    const t = window.setTimeout(() => inputRef.current?.focus(), 0)
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      if (buttonRef.current?.contains(target) || panelRef.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    window.addEventListener('scroll', updatePos, true)
    window.addEventListener('resize', updatePos)
    return () => {
      window.clearTimeout(t)
      document.removeEventListener('mousedown', handler)
      window.removeEventListener('scroll', updatePos, true)
      window.removeEventListener('resize', updatePos)
    }
  }, [open, updatePos])

  // Keep the active index in range as the filter narrows (and reset to "none" when empty).
  useEffect(() => {
    setActiveIdx((i) => (i < 0 ? -1 : Math.min(i, matches.length - 1)))
  }, [matches.length])

  const activeProperty = activeIdx >= 0 ? matches[activeIdx] : undefined

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIdx((i) => (matches.length === 0 ? -1 : (i + 1) % matches.length))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIdx((i) => (matches.length === 0 ? -1 : (i <= 0 ? matches.length - 1 : i - 1)))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (activeProperty) {
        setOpen(false)
        // SPA navigation into the property (Inertia visit — the keyboard equivalent of the <Link> rows).
        router.visit(activeProperty.basePath)
      }
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setOpen(false)
      buttonRef.current?.focus()
    }
  }

  // Add property is scoped to the current business.
  const addPropertyHref = scopedGroup ? `/onboarding?account_id=${scopedGroup.accountId}` : '/onboarding'

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setOpen(!open)}
        aria-label={isProperty ? 'Switch property' : 'Jump to property'}
        aria-expanded={open}
        className="flex items-center gap-1.5 rounded-lg border border-border/60 bg-white px-3 py-1.5 text-xs font-medium text-foreground hover:border-border transition-colors"
      >
        {isProperty ? (
          // property mode: keep the familiar "shows current property" trigger, plus the ⌘K kbd
          // so the newly-shared shortcut is discoverable.
          <span className="max-w-[140px] truncate">{currentProperty?.name ?? 'Jump to property'}</span>
        ) : (
          <>
            <Search className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="hidden sm:inline-block">Jump to property</span>
          </>
        )}
        <kbd className="hidden items-center gap-0.5 rounded border border-border/60 bg-muted px-1 font-mono text-[10px] font-medium text-muted-foreground sm:inline-flex">
          ⌘K
        </kbd>
        <ChevronDown className={cn('h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200', open && 'rotate-180')} />
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            className="fixed z-[9999] w-80 overflow-hidden rounded-xl border border-border/60 bg-white shadow-xl shadow-foreground/10 animate-slide-in"
            style={{ top: pos.top, right: pos.right }}
          >
            {/* Search field */}
            <div className="flex items-center gap-2 border-b border-border/40 px-3 py-2.5">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onInputKeyDown}
                placeholder="Search properties…"
                className="w-full bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
              />
            </div>

            <div ref={listRef} className="max-h-80 overflow-y-auto py-1">
              {rows.length === 0 ? (
                <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                  {flat.length === 0 ? 'No properties yet' : 'No matches'}
                </div>
              ) : (
                rows.map((row, idx) => {
                  const p = row.property
                  const isKeyboardActive = activeProperty?.id === p.id
                  // property mode highlights the current property — but the keyboard/hover row
                  // takes precedence so arrow-key navigation still moves the highlight visually.
                  const isCurrent = isProperty && currentProperty?.id === p.id
                  const isActive = isKeyboardActive || (isCurrent && activeIdx < 0)
                  const nested = row.depth === 1
                  return (
                    <div key={p.id} className="relative">
                      {/* The rail that ties a group's members to their lead. */}
                      {nested && (
                        <span
                          className={cn('absolute left-[19px] w-px bg-border', row.lastInGroup ? 'top-0 h-[18px]' : 'inset-y-0')}
                          aria-hidden="true"
                        />
                      )}
                      {nested && <span className="absolute left-[19px] top-[18px] h-px w-2.5 bg-border" aria-hidden="true" />}
                      <Link
                        href={p.basePath}
                        onClick={() => setOpen(false)}
                        onMouseEnter={() => setActiveIdx(idx)}
                        onMouseLeave={() => setActiveIdx(-1)}
                        className={cn(
                          'flex items-center justify-between gap-2 py-2 pr-3 text-sm transition-colors',
                          nested ? 'pl-9' : 'pl-3',
                          isActive ? 'bg-primary/5 text-primary' : 'text-foreground hover:bg-muted',
                        )}
                      >
                        <span className="flex min-w-0 items-center gap-1.5">
                          {/* The crown marks the hotel that owns the whole unit's pricing. */}
                          {row.isLead && <Crown className="h-3 w-3 shrink-0 text-primary" aria-hidden="true" />}
                          <span className="min-w-0">
                            <span className={cn('block truncate font-medium', isActive && 'font-semibold')}>{p.name}</span>
                            {/* A grouped row always says its role: nested under its lead the caption is
                                context; flat (searching, or an unlisted lead) it is the only clue there is
                                one at all. A combined inventory is called by its lead listing's name, so a
                                following listing names it and the lead, which IS that name, does not. */}
                            {row.groupName && (
                              <span className={cn('block truncate text-[10px]', isActive ? 'text-primary/70' : 'text-muted-foreground')}>
                                {row.isLead ? 'Lead listing' : `Following listing · ${row.groupName}`}
                              </span>
                            )}
                          </span>
                        </span>
                        <span className="flex shrink-0 items-center gap-1.5">
                          <span className={cn('text-[10px] uppercase tracking-widest', isActive ? 'text-primary/70' : 'text-muted-foreground')}>
                            {p.currency || ''}
                          </span>
                          {/* keyboard-active row shows the ↵ affordance; the current property (when
                              it isn't the keyboard row) shows a ✓ to mark where you already are. */}
                          {isKeyboardActive ? (
                            <CornerDownLeft className="h-3.5 w-3.5 text-primary/70" />
                          ) : (
                            isCurrent && <Check className="h-3.5 w-3.5 text-primary/70" />
                          )}
                        </span>
                      </Link>
                    </div>
                  )
                })
              )}
            </div>

            <Link
              href={addPropertyHref}
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 border-t border-border/40 px-3 py-2.5 text-xs font-medium text-primary hover:bg-muted transition-colors"
            >
              <Plus className="h-3.5 w-3.5" />
              Add property
            </Link>
          </div>,
          document.body,
        )}
    </>
  )
}
